import { useEffect, useId, useState } from "react";
import { useBrand } from "@/lib/brand";
import GpsOdometer from "@/components/GpsOdometer";
import PhotoUploader from "@/components/PhotoUploader";
import SignaturePad from "@/components/SignaturePad";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api, formatError } from "@/lib/api";
import { toast } from "sonner";
import { Save } from "lucide-react";
import { allChecked, equipmentOf, FUEL_LEVELS } from "@/lib/equipment";
import { sinLoDeLaEntrega } from "@/lib/registro";

// La escala de combustible es una sola (lib/equipment); se reexporta para la recogida autónoma.
export { FUEL_LEVELS };
export const WATER_LEVELS = ["lleno", "medio", "vacío"];


const PHOTOS_REQUIRED = 8;


export default function HandoverForm({ reservation, onSaved }) {
  const brand = useBrand();
  const uid = useId();
  const items = equipmentOf(reservation);
  const [loading, setLoading] = useState(false);
  // Lo que falta para comunicar la entrega al registro de viajeros, sin lo que pone este mismo formulario (el
  // kilometraje y la hora). Solo avisa: la entrega se registra igualmente y la comunicación queda pendiente (la
  // tarjeta «Registro de viajeros» de la ficha lo enseña). Si no se puede leer, nada.
  const [faltaRegistro, setFaltaRegistro] = useState([]);
  useEffect(() => {
    let vivo = true;
    api.get(`/reservations/${reservation.id}/registry`)
      .then(({ data }) => {
        if (vivo) setFaltaRegistro(data?.enabled && (data.channel || data.blocked) ? sinLoDeLaEntrega(data.missing.delivery) : []);
      })
      .catch(() => { if (vivo) setFaltaRegistro([]); });
    return () => { vivo = false; };
  }, [reservation]);
  const [data, setData] = useState({
    km_start: "",
    fuel_level: "lleno",
    tire_state: "Bueno",
    interior_clean: true,
    exterior_clean: true,
    fresh_water_level: "lleno",
    grey_water_emptied: true,
    black_water_emptied: true,
    equipment: allChecked(items),
    // Prefill from the pre-checkin: the client already provided the second
    // driver's data and documentation (free of charge); the owner just
    // verifies it at handover.
    additional_driver: reservation.additional_driver_requested
      ? {
          full_name: reservation.checkin_data?.additional_driver?.full_name || "",
          dni_number: reservation.checkin_data?.additional_driver?.dni_number || "",
          license_number: reservation.checkin_data?.additional_driver?.license_number || "",
          license_issue_date: reservation.checkin_data?.additional_driver?.license_issue_date || "",
        }
      : null,
    photos: [],
    damage_notes: "",
    damage_photos: [],
    traveler_signature: "",
    owner_signature: "",
  });

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const setEq = (k, v) =>
    setData((d) => ({ ...d, equipment: { ...d.equipment, [k]: v } }));
  const setDriver = (k, v) =>
    setData((d) => ({
      ...d,
      additional_driver: { ...(d.additional_driver || {}), [k]: v },
    }));

  const valid = () => {
    if (data.km_start === "" || Number(data.km_start) < 0) return "Falta el kilometraje de salida";
    if (data.photos.length < PHOTOS_REQUIRED) return `Faltan fotos (${data.photos.length}/${PHOTOS_REQUIRED} mínimo)`;
    if (!data.traveler_signature) return "Falta la firma del viajero";
    if (reservation.additional_driver_requested) {
      const d = data.additional_driver || {};
      if (!d.full_name || !d.dni_number || !d.license_number) {
        return "Faltan datos del conductor adicional";
      }
    }
    return null;
  };

  const submit = async () => {
    const err = valid();
    if (err) {
      toast.error(err);
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...data,
        km_start: Number(data.km_start),
        additional_driver: reservation.additional_driver_requested
          ? data.additional_driver
          : null,
      };
      await api.post(`/reservations/${reservation.id}/handover`, payload);
      toast.success("Entrega registrada. Contrato listo.");
      onSaved?.();
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="handover-form">
      {faltaRegistro.length > 0 && (
        <div className="bg-[#E89B33]/10 border border-[#E89B33]/30 rounded-xl p-4 text-sm text-[#7A4E0E]" data-testid="handover-registry-missing">
          <strong>Registro de viajeros:</strong> para comunicar esta entrega falta:
          <ul className="list-disc pl-5 mt-1">{faltaRegistro.map((t, i) => <li key={i}>{t}</li>)}</ul>
          Puedes registrar la entrega igualmente: la comunicación queda pendiente; lo que falta se completa en la tarjeta
          «Registro de viajeros» de la reserva (o donde diga cada línea).
        </div>
      )}
      <div className="bg-white border border-[#DCD8D0] rounded-xl p-6">
        <h3 className="font-display text-lg font-semibold text-[#1A1A1A] mb-4">Estado del vehículo</h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div>
            <Label>Km salida</Label>
            <Input type="number" min="0" value={data.km_start} onChange={(e) => set("km_start", e.target.value)} className="mt-2 bg-[#F7F5F0] border-[#DCD8D0]" data-testid="ho-km-start" />
            <GpsOdometer reservationId={reservation.id} onValue={(km) => set("km_start", String(km))} onFuel={(f) => set("fuel_level", f)} current={data.km_start} testId="ho-gps" />
          </div>
          <div>
            <Label>Combustible</Label>
            <Select value={data.fuel_level} onValueChange={(v) => set("fuel_level", v)}>
              <SelectTrigger className="mt-2 bg-[#F7F5F0] border-[#DCD8D0]" data-testid="ho-fuel">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {FUEL_LEVELS.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Agua limpia</Label>
            <Select value={data.fresh_water_level} onValueChange={(v) => set("fresh_water_level", v)}>
              <SelectTrigger className="mt-2 bg-[#F7F5F0] border-[#DCD8D0]" data-testid="ho-water">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {WATER_LEVELS.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Estado neumáticos</Label>
            <Input value={data.tire_state} onChange={(e) => set("tire_state", e.target.value)} className="mt-2 bg-[#F7F5F0] border-[#DCD8D0]" data-testid="ho-tires" />
          </div>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2 mt-4">
          {[
            ["interior_clean", "Interior limpio"],
            ["exterior_clean", "Exterior limpio"],
            ["grey_water_emptied", "Aguas grises vaciadas"],
            ["black_water_emptied", "Aguas negras vaciadas"],
          ].map(([key, label]) => (
            <label key={key} className="flex items-center gap-3 bg-[#F7F5F0] border border-[#DCD8D0] rounded-lg p-3 cursor-pointer text-sm">
              <Checkbox checked={data[key]} onCheckedChange={(v) => set(key, Boolean(v))} data-testid={`ho-${key}`} />
              <span>{label}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="bg-white border border-[#DCD8D0] rounded-xl p-6">
        <h3 className="font-display text-lg font-semibold text-[#1A1A1A] mb-4">Equipamiento verificado</h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {items.map(({ key, label }) => (
            <label key={key} className="flex items-center justify-between gap-3 bg-[#F7F5F0] border border-[#DCD8D0] rounded-lg p-2.5 cursor-pointer text-sm">
              <span>{label}</span>
              <Checkbox checked={data.equipment[key]} onCheckedChange={(v) => setEq(key, Boolean(v))} data-testid={`ho-eq-${key}`} />
            </label>
          ))}
        </div>
      </div>

      {reservation.additional_driver_requested && (
        <div className="bg-white border border-[#DCD8D0] rounded-xl p-6">
          <h3 className="font-display text-lg font-semibold text-[#1A1A1A] mb-2">Conductor adicional</h3>
          <p className="text-sm text-[#5C5C5C] mb-4">El cliente solicitó conductor adicional en el Pre-Checkin. Registra aquí sus datos.</p>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label htmlFor={`${uid}-ad-name`}>Nombre y apellidos</Label>
              <Input id={`${uid}-ad-name`} value={data.additional_driver?.full_name || ""} onChange={(e) => setDriver("full_name", e.target.value)} className="mt-2 bg-[#F7F5F0] border-[#DCD8D0]" data-testid="ho-ad-name" />
            </div>
            <div>
              <Label htmlFor={`${uid}-ad-dni`}>DNI / NIE</Label>
              <Input id={`${uid}-ad-dni`} value={data.additional_driver?.dni_number || ""} onChange={(e) => setDriver("dni_number", e.target.value)} className="mt-2 bg-[#F7F5F0] border-[#DCD8D0]" data-testid="ho-ad-dni" />
            </div>
            <div>
              <Label htmlFor={`${uid}-ad-license`}>Nº de carné</Label>
              <Input id={`${uid}-ad-license`} value={data.additional_driver?.license_number || ""} onChange={(e) => setDriver("license_number", e.target.value)} className="mt-2 bg-[#F7F5F0] border-[#DCD8D0]" data-testid="ho-ad-license" />
            </div>
            <div>
              <Label htmlFor={`${uid}-ad-issue`}>Carné obtenido el</Label>
              <Input id={`${uid}-ad-issue`} type="date" value={data.additional_driver?.license_issue_date || ""} onChange={(e) => setDriver("license_issue_date", e.target.value)} className="mt-2 bg-[#F7F5F0] border-[#DCD8D0]" data-testid="ho-ad-issue" />
            </div>
          </div>
        </div>
      )}

      <div className="bg-white border border-[#DCD8D0] rounded-xl p-6">
        <h3 className="font-display text-lg font-semibold text-[#1A1A1A] mb-1">Fotografías de entrega</h3>
        <p className="text-sm text-[#5C5C5C] mb-3">
          Mínimo {PHOTOS_REQUIRED}: los 4 lados exteriores, techo, interior, salpicadero con km visible y ruedas.
        </p>
        <PhotoUploader
          label={`Fotos (${data.photos.length}/${PHOTOS_REQUIRED}+)`}
          value={data.photos}
          onChange={(v) => set("photos", v)}
          multiple
          adminUpload
          reservationId={reservation.id}
          testid="ho-photos"
        />
      </div>

      <div className="bg-white border border-[#DCD8D0] rounded-xl p-6">
        <h3 className="font-display text-lg font-semibold text-[#1A1A1A] mb-1">Daños previos</h3>
        <p className="text-sm text-[#5C5C5C] mb-3">Describe cada daño preexistente y adjunta fotos de cada uno como evidencia.</p>
        <Textarea
          value={data.damage_notes}
          onChange={(e) => set("damage_notes", e.target.value)}
          rows={3}
          placeholder="Ej. Rayón en parachoques trasero lado izq."
          className="bg-[#F7F5F0] border-[#DCD8D0] mb-3"
          data-testid="ho-damage-notes"
        />
        <PhotoUploader
          label="Fotos de daños previos (opcional)"
          value={data.damage_photos}
          onChange={(v) => set("damage_photos", v)}
          multiple
          adminUpload
          reservationId={reservation.id}
          testid="ho-damage-photos"
        />
      </div>

      <div className="bg-white border border-[#DCD8D0] rounded-xl p-6">
        <h3 className="font-display text-lg font-semibold text-[#1A1A1A] mb-4">Firmas de entrega</h3>
        <div className="grid lg:grid-cols-2 gap-6">
          <div>
            <Label>Firma del viajero ({reservation.client_name})</Label>
            <div className="mt-2">
              <SignaturePad value={data.traveler_signature} onChange={(v) => set("traveler_signature", v)} testid="ho-sig-traveler" />
            </div>
          </div>
          <div>
            <Label>Firma del propietario ({brand?.brand_name || "empresa"})</Label>
            <div className="mt-2">
              <SignaturePad value={data.owner_signature} onChange={(v) => set("owner_signature", v)} testid="ho-sig-owner" />
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <Button
          onClick={submit}
          disabled={loading}
          className="bg-marca hover:bg-marca-hover text-white"
          data-testid="ho-submit"
        >
          <Save className="h-4 w-4 mr-2" />
          {loading ? "Guardando..." : "Cerrar entrega y generar contrato"}
        </Button>
      </div>
    </div>
  );
}
